/**
 * System Architecture Validation
 * Run this to check data model consistency
 *
 * Usage: In browser console, run: validateSystem()
 */

import { allObjectFields } from '../services/mockData/objectManagerFields';

interface ValidationResult {
  category: string;
  check: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  details?: any;
}

// Object API name to Object ID mapping
const objectApiToId: Record<string, string> = {
  'Lead': 'obj_lead',
  'Client': 'obj_client',
  'Contact': 'obj_contact',
  'Opportunity': 'obj_opportunity',
  'Case': 'obj_case',
  'Task': 'obj_task',
  'Event': 'obj_event',
  'Report': 'obj_report',
  'User': 'obj_user'
};

// Storage key mapping
const objectStorageKeys: Record<string, string> = {
  'Lead': 'crm_lead_records',
  'Client': 'crm_client_records',
  'Contact': 'crm_contact_records',
  'Opportunity': 'crm_opportunity_records',
  'Case': 'crm_case_records',
  'Task': 'crm_task_records',
  'Event': 'crm_event_records',
  'Report': 'crm_report_records'
};

export class SystemValidator {
  private results: ValidationResult[] = [];

  /**
   * Run all validation checks
   */
  public validate(): ValidationResult[] {
    console.log('🔍 SYSTEM VALIDATION STARTING...\n');

    this.results = [];

    // Check 1: Object Manager Integrity
    this.validateObjectManager();

    // Check 2: List View Consistency
    this.validateListViews();

    // Check 3: Workflow Field References
    this.validateWorkflowFields();

    // Check 4: Record Data Consistency
    this.validateRecords();

    // Print results
    this.printResults();

    return this.results;
  }

  /**
   * Check 1: Object Manager has all required fields
   */
  private validateObjectManager(): void {
    const requiredSystemFields = ['id', 'createdDate', 'createdBy', 'lastModifiedDate', 'lastModifiedBy', 'owner'];
    const standardObjects = ['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Task', 'Event', 'Report'];

    standardObjects.forEach(objectName => {
      const objectId = objectApiToId[objectName];
      const fields = allObjectFields[objectId];

      if (!fields) {
        this.addResult('Object Manager', `${objectName} exists`, 'fail', `Object ${objectName} not found in Object Manager`);
        return;
      }

      // Check system fields
      requiredSystemFields.forEach(fieldName => {
        const field = fields.find(f => f.fieldName === fieldName);

        if (!field) {
          this.addResult(
            'Object Manager',
            `${objectName}.${fieldName} field exists`,
            'fail',
            `System field "${fieldName}" missing from ${objectName}`
          );
        } else if (!field.isSystemField) {
          this.addResult(
            'Object Manager',
            `${objectName}.${fieldName} marked as system`,
            'warning',
            `Field "${fieldName}" exists but not marked as system field`
          );
        } else {
          this.addResult(
            'Object Manager',
            `${objectName}.${fieldName}`,
            'pass',
            `System field properly defined`
          );
        }
      });
    });
  }

  /**
   * Check 2: List views match Object Manager
   */
  private validateListViews(): void {
    const standardObjects = ['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Task', 'Event'];

    standardObjects.forEach(objectName => {
      const storageKey = `crm_${objectName.toLowerCase()}_list_views`;
      const listViewsData = localStorage.getItem(storageKey);

      if (!listViewsData) {
        this.addResult('List Views', `${objectName} list views`, 'warning', `No cached list views for ${objectName}`);
        return;
      }

      const views = JSON.parse(listViewsData);
      const objectId = objectApiToId[objectName];
      const objectFields = allObjectFields[objectId];

      if (!objectFields) return;

      views.forEach((view: any) => {
        view.columns?.forEach((column: any) => {
          const field = objectFields.find(f => f.fieldName === column.fieldApiName);

          if (!field) {
            this.addResult(
              'List Views',
              `${objectName}.${column.fieldApiName}`,
              'fail',
              `Column "${column.fieldApiName}" not found in Object Manager for ${objectName}`,
              { view: view.name, column: column.fieldApiName }
            );
          } else {
            this.addResult(
              'List Views',
              `${objectName}.${column.fieldApiName}`,
              'pass',
              `Column matches Object Manager field`
            );
          }
        });
      });
    });
  }

  /**
   * Check 3: Workflow fields exist in Object Manager
   */
  private validateWorkflowFields(): void {
    const workflowsData = localStorage.getItem('crm_workflow_rules');

    if (!workflowsData) {
      this.addResult('Workflows', 'Workflow data', 'warning', 'No workflows found in storage');
      return;
    }

    const workflows = JSON.parse(workflowsData);

    workflows.forEach((workflow: any) => {
      const objectId = objectApiToId[workflow.objectApiName];
      const objectFields = allObjectFields[objectId];

      if (!objectFields) {
        this.addResult(
          'Workflows',
          `${workflow.name} object reference`,
          'fail',
          `Workflow references non-existent object: ${workflow.objectApiName}`
        );
        return;
      }

      // Check conditions
      workflow.evaluationCriteria?.conditions?.forEach((condition: any) => {
        const field = objectFields.find(f => f.fieldName === condition.fieldApiName);

        if (!field) {
          this.addResult(
            'Workflows',
            `${workflow.name} condition field`,
            'fail',
            `Condition references non-existent field: ${condition.fieldApiName}`,
            { workflow: workflow.name, field: condition.fieldApiName }
          );
        } else {
          this.addResult(
            'Workflows',
            `${workflow.name} condition`,
            'pass',
            `Condition field exists in Object Manager`
          );
        }
      });

      // Check actions
      workflow.actions?.forEach((action: any) => {
        if (action.type === 'fieldUpdate') {
          const field = objectFields.find(f => f.fieldName === action.config.fieldApiName);

          if (!field) {
            this.addResult(
              'Workflows',
              `${workflow.name} action field`,
              'fail',
              `Field update references non-existent field: ${action.config.fieldApiName}`,
              { workflow: workflow.name, field: action.config.fieldApiName }
            );
          } else {
            this.addResult(
              'Workflows',
              `${workflow.name} action`,
              'pass',
              `Action field exists in Object Manager`
            );
          }
        }
      });
    });
  }

  /**
   * Check 4: Records have required system fields
   */
  private validateRecords(): void {
    const standardObjects = ['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Task', 'Event', 'Report'];

    standardObjects.forEach(objectName => {
      const storageKey = objectStorageKeys[objectName];
      const recordsData = localStorage.getItem(storageKey);

      if (!recordsData) {
        this.addResult('Records', `${objectName} records`, 'warning', `No records found for ${objectName}`);
        return;
      }

      const records = JSON.parse(recordsData);

      if (records.length === 0) {
        this.addResult('Records', `${objectName} records`, 'warning', `No records found for ${objectName}`);
        return;
      }

      const firstRecord = records[0];

      // Check if record has ID
      if (!firstRecord.id) {
        this.addResult('Records', `${objectName} has IDs`, 'fail', `Records missing ID field`);
      } else {
        this.addResult('Records', `${objectName} has IDs`, 'pass', `All records have IDs (e.g., ${firstRecord.id})`);
      }

      // Check if record has system fields
      const systemFields = ['createdDate', 'owner'];
      systemFields.forEach(fieldName => {
        if (!firstRecord[fieldName]) {
          this.addResult(
            'Records',
            `${objectName}.${fieldName}`,
            'warning',
            `Records missing system field: ${fieldName}`
          );
        }
      });
    });
  }

  /**
   * Add validation result
   */
  private addResult(category: string, check: string, status: 'pass' | 'fail' | 'warning', message: string, details?: any): void {
    this.results.push({ category, check, status, message, details });
  }

  /**
   * Print validation results
   */
  private printResults(): void {
    console.log('\n═══════════════════════════════════════════════════════════');
    console.log('SYSTEM VALIDATION RESULTS');
    console.log('═══════════════════════════════════════════════════════════\n');

    const byCategory = this.groupBy(this.results, 'category');

    Object.entries(byCategory).forEach(([category, results]: [string, ValidationResult[]]) => {
      console.log(`\n📋 ${category}`);
      console.log('─'.repeat(60));

      results.forEach(result => {
        const icon = result.status === 'pass' ? '✅' : result.status === 'fail' ? '❌' : '⚠️';
        console.log(`${icon} ${result.check}`);
        console.log(`   ${result.message}`);
        if (result.details) {
          console.log(`   Details:`, result.details);
        }
      });
    });

    // Summary
    const total = this.results.length;
    const passed = this.results.filter(r => r.status === 'pass').length;
    const failed = this.results.filter(r => r.status === 'fail').length;
    const warnings = this.results.filter(r => r.status === 'warning').length;

    console.log('\n═══════════════════════════════════════════════════════════');
    console.log('SUMMARY');
    console.log('═══════════════════════════════════════════════════════════');
    console.log(`Total Checks: ${total}`);
    console.log(`✅ Passed: ${passed}`);
    console.log(`❌ Failed: ${failed}`);
    console.log(`⚠️  Warnings: ${warnings}`);

    if (failed === 0) {
      console.log('\n🎉 SYSTEM VALIDATION PASSED!');
    } else {
      console.log('\n🚨 SYSTEM VALIDATION FAILED - Fix errors before deploying!');
    }

    console.log('═══════════════════════════════════════════════════════════\n');
  }

  /**
   * Group results by category
   */
  private groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
    return array.reduce((result, item) => {
      const group = String(item[key]);
      if (!result[group]) result[group] = [];
      result[group].push(item);
      return result;
    }, {} as Record<string, T[]>);
  }
}

// Export singleton
export const systemValidator = new SystemValidator();

// Browser console helper
if (typeof window !== 'undefined') {
  (window as any).validateSystem = () => systemValidator.validate();
}
